/**
 * Utility functions for error handling and message formatting
 */

/**
 * Cleans Firebase error messages by removing the "Firebase: Error" prefix
 * and extracting the actual error message
 * @param error - The error object or message
 * @returns Clean error message
 */
export const cleanFirebaseError = (error: any): string => {
  if (!error) return 'An unknown error occurred';
  
  let errorMessage = '';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error.message) {
    errorMessage = error.message;
  } else if (error.code) {
    errorMessage = error.code;
  } else {
    errorMessage = 'An unknown error occurred';
  }
  
  // Remove Firebase error prefixes
  errorMessage = errorMessage
    .replace(/^Firebase:\s*Error\s*\([^)]+\)\.?\s*/i, '') // Remove "Firebase: Error (auth/...)."
    .replace(/^Firebase:\s*/i, '') // Remove "Firebase:"
    .replace(/^Error\s*\([^)]+\)\.?\s*/i, '') // Remove "Error (auth/...)."
    .trim();
  
  // If the message is empty after cleaning, return a generic message
  if (!errorMessage) {
    return 'An error occurred. Please try again.';
  }
  
  return errorMessage;
};

/**
 * Maps Firebase error codes to user-friendly messages
 * @param errorCode - Firebase error code
 * @returns User-friendly error message
 */
export const getFirebaseErrorMessage = (errorCode: string): string => {
  const errorMessages: Record<string, string> = {
    'auth/invalid-credential': 'Invalid email or password',
    'auth/user-not-found': 'No account found with this email',
    'auth/wrong-password': 'Incorrect password',
    'auth/email-already-in-use': 'An account with this email already exists',
    'auth/weak-password': 'Password should be at least 6 characters',
    'auth/invalid-email': 'Please enter a valid email address',
    'auth/user-disabled': 'This account has been disabled',
    'auth/too-many-requests': 'Too many attempts. Please try again later',
    'auth/network-request-failed': 'Network error. Please check your connection',
    'auth/operation-not-allowed': 'This operation is not allowed',
    'auth/requires-recent-login': 'Please log in again to continue',
    'auth/invalid-verification-code': 'Invalid verification code',
    'auth/invalid-verification-id': 'Invalid verification ID',
    'auth/missing-verification-code': 'Verification code is required',
    'auth/missing-verification-id': 'Verification ID is required',
    'auth/code-expired': 'Verification code has expired',
    'auth/invalid-action-code': 'Invalid or expired reset link',
    'auth/expired-action-code': 'Reset link has expired',
    'auth/invalid-continue-uri': 'Invalid continue URL',
    'auth/missing-continue-uri': 'Continue URL is required',
    'auth/unauthorized-domain': 'This domain is not authorized',
    'auth/invalid-dynamic-link-domain': 'Invalid dynamic link domain',
    'auth/credential-already-in-use': 'This credential is already associated with another account',
    'auth/invalid-phone-number': 'Invalid phone number',
    'auth/missing-phone-number': 'Phone number is required',
    'auth/quota-exceeded': 'Quota exceeded. Please try again later',
    'auth/captcha-check-failed': 'reCAPTCHA verification failed',
    'auth/invalid-app-credential': 'Invalid app credential',
    'auth/missing-app-credential': 'App credential is required',
    'auth/invalid-tenant-id': 'Invalid tenant ID',
    'auth/tenant-id-mismatch': 'Tenant ID mismatch',
    'auth/unsupported-tenant-operation': 'Unsupported tenant operation',
    'auth/admin-restricted-operation': 'This operation is restricted to administrators',
    'auth/app-not-authorized': 'App is not authorized',
    'auth/argument-error': 'Invalid argument provided',
    'auth/invalid-api-key': 'Invalid API key',
    'auth/invalid-user-token': 'Invalid user token',
    'auth/network-request-failed': 'Network request failed',
    'auth/operation-not-allowed': 'Operation not allowed',
    'auth/requires-recent-login': 'Recent login required',
    'auth/too-many-requests': 'Too many requests',
    'auth/uid-already-exists': 'User ID already exists',
    'auth/user-token-expired': 'User token has expired',
    'auth/web-storage-unsupported': 'Web storage is not supported'
  };
  
  return errorMessages[errorCode] || cleanFirebaseError(errorCode);
};

/**
 * Gets a clean, user-friendly error message from any error
 * @param error - The error object
 * @returns Clean error message
 */
export const getCleanErrorMessage = (error: any): string => {
  if (!error) return 'An unknown error occurred';
  
  // If it's a Firebase error with a code, use the mapped message
  if (error.code && error.code.startsWith('auth/')) {
    return getFirebaseErrorMessage(error.code);
  }
  
  // Otherwise, clean the error message
  return cleanFirebaseError(error);
};
